import { TRANSLATIONS } from '../constants/translations.ts';
import { CONFIG } from '../constants/config.ts';

type Lang = string; // ISO-639-1

export class TranslationService {
    private currentLang: Lang = CONFIG.DEFAULT_LANG;
    private translations: Record<string, string> = {};

    private apiKey: string | null = null;
    private _phraseCache = new Map<string, string>();
    constructor(apiKey?: string) {
        this.apiKey = apiKey ?? null;
        console.log('[TranslationService] Constructor - API key:', this.apiKey ? 'Present' : 'Missing', 'Length:', this.apiKey?.length || 0);
    }

    async smartTranslate(text: string, targetLang: string): Promise<string> {
        if (!text) return text;

        const cacheKey = `${text}::${targetLang}`;
        const cached = this._phraseCache.get(cacheKey);
        if (cached) return cached;

        // Один короткий prompt → GPT
        const translated = await this.translateText(text, targetLang);
        this._phraseCache.set(cacheKey, translated);
        return translated;
    }

    /** Smart split badges when GPT doesn't use proper separators */
    private smartSplitBadges(text: string, expectedCount: number): string[] {
        // If we only have one long string but need multiple badges, 
        // try to intelligently split it
        if (expectedCount <= 1) return [text];
        
        // Split by word boundaries and try to group words into the expected number of parts
        const words = text.split(/\s+/).filter(w => w.length > 0);
        if (words.length < expectedCount) {
            // Not enough words, return what we have
            return words.concat(Array(expectedCount - words.length).fill('Field'));
        }
        
        // Try to distribute words evenly across expected count
        const result: string[] = [];
        const wordsPerBadge = Math.ceil(words.length / expectedCount);
        
        for (let i = 0; i < expectedCount; i++) {
            const start = i * wordsPerBadge;
            const end = Math.min(start + wordsPerBadge, words.length);
            const badgeWords = words.slice(start, end);
            
            if (badgeWords.length > 0) {
                result.push(badgeWords.join(' '));
            } else {
                result.push('Field');
            }
        }
        
        console.log(`[TranslationService] 🧠 Smart split result:`, result);
        return result;
    }

    /** Универсальный перевод короткой фразы (до 100-150 симв.) */
    private async translateText(text: string, targetLang: string): Promise<string> {
        if (!this.apiKey) {
            console.warn('[TranslationService] No API-key – возвращаем оригинал');
            return text;
        }

        try {
            const res = await fetch(CONFIG.TRANSLATE_TEXT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text,
                    targetLang
                })
            });

            const data = await res.json();
            const translated = (data.reply || '').trim();
            return translated || text;        // fallback: если GPT вернул пусто
        } catch (err) {
            console.error('[TranslationService] translateText error:', err);
            return text;                      // fallback: оригинальный текст
        }
    }

    async initialize(): Promise<void> {
        const browserLang = navigator.language.split('-')[0];
        this.currentLang =
            CONFIG.SUPPORTED_LANGS.includes(browserLang)
                ? browserLang
                : CONFIG.DEFAULT_LANG;

        await this.loadTranslations(this.currentLang);
    }

    private async loadTranslations(lang: Lang): Promise<void> {
        this.translations = TRANSLATIONS[lang] ?? TRANSLATIONS[CONFIG.DEFAULT_LANG] ?? {};
    }

    t(key: string, params?: Record<string, any>): string {
        let txt = this.translations[key] ?? key;
        if (params) {
            for (const [k, v] of Object.entries(params))
                txt = txt.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
        return txt;
    }


    /** ───────── GPT-определение языка на основе ВСЕХ полей формы ──────── */
    async detectFormLanguage(fields: any[]): Promise<Lang> {
        if (!fields || fields.length === 0) {
            console.log('[TranslationService] No fields provided, defaulting to', CONFIG.DEFAULT_LANG);
            return CONFIG.DEFAULT_LANG;
        }

        // Собираем ВСЕ текстовые данные формы для анализа
        const formData = this.collectAllFormData(fields);
        
        if (!formData.trim()) {
            console.log('[TranslationService] No visible text in form, defaulting to', CONFIG.DEFAULT_LANG);
            return CONFIG.DEFAULT_LANG;
        }

        console.log('[TranslationService] Detecting language for form data');
        return await this.askGptForLanguage(formData);
    }

    /** Собирает ВСЕ текстовые данные из формы */
    private collectAllFormData(fields: any[]): string {
        const allText: string[] = [];

        fields.forEach((field, index) => {
            const fieldData: string[] = [];
            
            // Собираем все возможные текстовые данные поля
            if (field.label) fieldData.push(`Label: "${field.label}"`);
            if (field.placeholder) fieldData.push(`Placeholder: "${field.placeholder}"`);
            if (field.title) fieldData.push(`Title: "${field.title}"`);
            if (field.ariaLabel) fieldData.push(`AriaLabel: "${field.ariaLabel}"`);
            if (field.type) fieldData.push(`Type: ${field.type}`);
            
            // Для select - добавляем опции
            if (field.options && Array.isArray(field.options)) {
                const optionTexts = field.options
                    .map((opt: any) => opt.text || opt.label || opt.value)
                    .filter(Boolean)
                    .slice(0, 3); // Первые 3 опции для экономии
                if (optionTexts.length > 0) {
                    fieldData.push(`Options: [${optionTexts.join(', ')}]`);
                }
            }

            if (fieldData.length > 0) {
                allText.push(`Field ${index + 1}: ${fieldData.join(', ')}`);
            }
        });

        return allText.join('\n');
    }

    async generateSmartBadges(fields: any[], userLanguage: string): Promise<string[]> {
        console.log(`[TranslationService] 🎯 BADGE GENERATION: ${fields.length} fields for ${userLanguage}`);
        console.log(`[TranslationService] 🎯 Field names being processed:`, fields.map(f => f.name));
        
        if (fields.length === 0) return [];

        try {
            // Формируем расширенное описание полей для GPT с максимальным контекстом
            const fieldsInfo = fields.map((field, index) => {
                const parts = [];
                
                // Базовые идентификаторы (КРИТИЧНО для понимания семантики)
                if (field.name) parts.push(`name="${field.name}"`);
                if (field.id && field.id !== field.name) parts.push(`id="${field.id}"`);
                
                // Тип и текущее значение
                if (field.type) parts.push(`type="${field.type}"`);
                if (field.value) parts.push(`value="${field.value}"`);
                
                // Все текстовые подсказки
                if (field.label) parts.push(`label="${field.label}"`);
                if (field.placeholder) parts.push(`placeholder="${field.placeholder}"`);
                if (field.title) parts.push(`title="${field.title}"`);
                if (field.ariaLabel) parts.push(`aria-label="${field.ariaLabel}"`);
                
                // Семантические CSS классы
                if (field.className) {
                    const semanticClasses = field.className.split(' ')
                        .filter((cls: string) => cls.includes('budget') || cls.includes('price') || 
                                     cls.includes('amount') || cls.includes('cost') ||
                                     cls.includes('name') || cls.includes('email') ||
                                     cls.includes('phone') || cls.includes('date') ||
                                     cls.includes('guest') || cls.includes('room'));
                    if (semanticClasses.length > 0) {
                        parts.push(`semantic-classes="${semanticClasses.join(' ')}"`);
                    }
                }
                
                // Контекстная информация
                if (field.required) parts.push(`required="true"`);
                if (field.pattern) parts.push(`pattern="${field.pattern}"`);
                if (field.min !== undefined) parts.push(`min="${field.min}"`);
                if (field.max !== undefined) parts.push(`max="${field.max}"`);
                
                return `${index + 1}. ${parts.join(', ')}`;
            }).join('\n');

            const response = await fetch(CONFIG.GENERATE_BADGES_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fieldsInfo,
                    userLanguage,
                    fieldsCount: fields.length
                })
            });

            const data = await response.json();
            const badges = data.reply?.trim() || '';
            console.log(`[TranslationService] 📥 Raw GPT response: "${badges}"`);
            console.log(`[TranslationService] 📥 Expected ${fields.length} fields`);
            
            // Try to parse GPT response - first try the correct format
            let badgeArray = badges.split('|||').map((b: string) => b.trim());
            console.log(`[TranslationService] 📥 Split by ||| into ${badgeArray.length} parts:`, badgeArray);
            
            // If GPT didn't use the correct separator, try alternative parsing
            if (badgeArray.length === 1 && badgeArray[0].length > 0 && fields.length > 1) {
                console.log(`[TranslationService] 🔄 GPT didn't use ||| separator, trying alternative parsing...`);
                
                // Try to split by common patterns that might indicate separate labels
                const alternatives = [
                    badges.split(/\s{2,}/), // Split by 2+ spaces
                    badges.split(/\n/), // Split by newlines
                    badges.split(/\s*\|\s*/), // Split by single |
                    badges.split(/(?<=[a-z])\s+(?=[A-Z])/), // Split before capital letters
                ];
                
                for (const alternative of alternatives) {
                    const trimmed = alternative.map((s: string) => s.trim()).filter((s: string) => s.length > 0);
                    if (trimmed.length === fields.length) {
                        console.log(`[TranslationService] ✅ Found working alternative:`, trimmed);
                        badgeArray = trimmed;
                        break;
                    }
                }
                
                // If still no good match, try smart splitting based on field count
                if (badgeArray.length !== fields.length) {
                    console.log(`[TranslationService] 🔧 Attempting smart split for ${fields.length} fields...`);
                    badgeArray = this.smartSplitBadges(badges, fields.length);
                }
            }
            
            // Fallback если GPT вернул неправильное количество
            while (badgeArray.length < fields.length) {
                const missingIndex = badgeArray.length;
                const field = fields[missingIndex];
                const fallbackLabel = this.generateFallbackBadge(field);
                badgeArray.push(fallbackLabel);
            }
            
            // Очищаем бэджи и применяем разумное ограничение
            const finalBadges = badgeArray.slice(0, fields.length).map((badge: string) => {
                let cleanBadge = badge.trim();
                
                // КРИТИЧНО: Убираем ВСЕ троеточия и точки в конце
                cleanBadge = cleanBadge.replace(/\.{3,}$/, '').replace(/\.$/, '').trim();
                
                // Только если КРИТИЧНО длинно (более 40 символов) - аккуратно сокращаем
                if (cleanBadge.length > 40) {
                    // Пытаемся найти разумное место обрезки (после слова)
                    const words = cleanBadge.split(' ');
                    let shortened = '';
                    for (const word of words) {
                        if ((shortened + word).length <= 35) {
                            shortened += (shortened ? ' ' : '') + word;
                        } else {
                            break;
                        }
                    }
                    cleanBadge = shortened || cleanBadge.substring(0, 35);
                }
                
                return cleanBadge;
            });
            
            // Результат генерации бэджей
            console.log('[TranslationService] 🏷️ BADGES RESULT:');
            finalBadges.forEach((badge: string, index: number) => {
                const field = fields[index];
                const lengthStatus = badge.length <= 15 ? '✅' : badge.length <= 25 ? '⚠️' : '❌';
                console.log(`[TranslationService] "${field?.name}" (${field?.type}) → "${badge}" (${badge.length} chars) ${lengthStatus}`);
            });
            
            return finalBadges;
        } catch (error) {
            console.error('[TranslationService] Smart badges generation error:', error);
            // Fallback - генерируем умные лейблы локально
            return fields.map(f => this.generateFallbackBadge(f));
        }
    }

    private generateFallbackBadge(field: any): string {
        // Универсальный fallback без хардкодов языков
        // Возвращаем самое понятное название поля
        const originalText = field.label || field.placeholder || field.name || 'Field';
        
        // Разумное ограничение - только если критично длинно
        if (originalText.length > 35) {
            const words = originalText.split(' ');
            let shortened = '';
            for (const word of words) {
                if ((shortened + word).length <= 30) {
                    shortened += (shortened ? ' ' : '') + word;
                } else {
                    break;
                }
            }
            return shortened || originalText.substring(0, 30);
        }
        
        return originalText;
    }

    getCurrentLang(): Lang { return this.currentLang; }

    /** GPT-анализ полей для выявления чувствительной информации */
    async detectSensitiveFields(fields: any[]): Promise<boolean[]> {
        
        if (!this.apiKey || fields.length === 0) {
            console.log('[TranslationService] No API key or empty fields, using fallback');
            return this.fallbackSensitiveDetection(fields);
        }

        // Предварительная фильтрация: исключаем поля выбора и приватные поля
        const isExcludedFieldType = (type: string, fieldName: string) => {
            const lowerType = type.toLowerCase();
            const lowerName = fieldName.toLowerCase();
            
            // Важные select поля для составных полей (например, units для Retention)
            const importantSelectFields = ['units', 'unit', 'period', 'interval', 'duration', 'time', 'frequency'];
            if (lowerType.includes('select') && importantSelectFields.some(important => lowerName.includes(important))) {
                return false; // Не исключаем важные select поля
            }
            
            return lowerType.includes('select') || 
                   lowerType.includes('checkbox') || 
                   lowerType.includes('radio') || 
                   lowerType.includes('choice') ||
                   lowerType.includes('option') ||
                   lowerType.includes('toggle') ||
                   lowerType.includes('switch');
        };
        const sensitiveFlags = fields.map((field) => {
            // Исключаем поля выбора (для UX - проще кликнуть чем диктовать)
            if (isExcludedFieldType(field.type, field.name)) {
                console.log(`[TranslationService] Excluding ${field.type} field "${field.name}" from badges`);
                return true; // Помечаем как "чувствительное" чтобы исключить из бэджей
            }
            
            // Исключаем поля с data-ai-private="true"
            if (field.isPrivate === true) {
                console.log(`[TranslationService] Excluding private field "${field.name}" from badges - marked as data-ai-private`);
                return true; // Помечаем как "чувствительное" чтобы исключить из бэджей
            }

            
            return false; // Будет проанализировано GPT
        });

        // Если все поля исключены, возвращаем результат сразу
        const fieldsToAnalyze = fields.filter((_, index) => !sensitiveFlags[index]);
        if (fieldsToAnalyze.length === 0) {
            console.log('[TranslationService] All fields excluded, no GPT analysis needed');
            return sensitiveFlags;
        }

        console.log(`[TranslationService] Making SENSITIVE FIELDS request for ${fieldsToAnalyze.length} fields`);
        
        try {
            // Формируем описание ТОЛЬКО неисключенных полей для GPT анализа
            const fieldsInfo = fieldsToAnalyze.map((field, index) => {
                const parts = [];
                if (field.name) parts.push(`name="${field.name}"`);
                if (field.type) parts.push(`type="${field.type}"`);
                if (field.label) parts.push(`label="${field.label}"`);
                if (field.placeholder) parts.push(`placeholder="${field.placeholder}"`);
                if (field.className) parts.push(`class="${field.className}"`);
                if (field.ariaLabel) parts.push(`aria-label="${field.ariaLabel}"`);
                return `${index + 1}. ${parts.join(', ')}`;
            }).join('\n');

            // Отправляем в GPT для анализа

            const response = await fetch(CONFIG.DETECT_SENSITIVE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fieldsInfo,
                    expectedLength: fieldsToAnalyze.length
                })
            });

            const data = await response.json();
            const result = data.reply?.trim() || '';
            
            // Парсим ответ GPT только для неисключенных полей
            const gptFlags = this.parseSensitiveResponse(result, fieldsToAnalyze.length);
            
            // Объединяем результаты: исключенные + GPT анализ
            let gptIndex = 0;
            const finalFlags = sensitiveFlags.map((isExcluded) => {
                if (isExcluded) {
                    return true; // Уже исключено
                } else {
                    const gptResult = gptFlags[gptIndex++] || false;
                    return gptResult; // Результат GPT анализа
                }
            });
            
            return finalFlags;

        } catch (error) {
            console.error('[TranslationService] Sensitive fields detection error:', error);
            return this.fallbackSensitiveDetection(fields);
        }
    }

    /** Парсинг ответа GPT для sensitive fields */
    private parseSensitiveResponse(response: string, expectedLength: number): boolean[] {
        try {
            // Извлекаем числа из ответа
            const numbers = response.match(/\d/g) || [];
            const result: boolean[] = [];
            
            // Конвертируем в boolean массив
            for (let i = 0; i < expectedLength; i++) {
                if (i < numbers.length) {
                    result.push(numbers[i] === '1');
                } else {
                    // Если GPT вернул меньше чисел - используем fallback
                    result.push(false); // По умолчанию считаем безопасным
                }
            }
            
            return result;
        } catch (error) {
            console.error('[TranslationService] Failed to parse sensitive response:', error);
            return new Array(expectedLength).fill(false);
        }
    }

    /** Fallback детекция чувствительных полей */
    private fallbackSensitiveDetection(fields: any[]): boolean[] {
        const isExcludedFieldType = (type: string, fieldName: string) => {
            const lowerType = type.toLowerCase();
            const lowerName = fieldName.toLowerCase();
            
            // Важные select поля для составных полей (например, units для Retention)
            const importantSelectFields = ['units', 'unit', 'period', 'interval', 'duration', 'time', 'frequency'];
            if (lowerType.includes('select') && importantSelectFields.some(important => lowerName.includes(important))) {
                return false; // Не исключаем важные select поля
            }
            
            return lowerType.includes('select') || 
                   lowerType.includes('checkbox') || 
                   lowerType.includes('radio') || 
                   lowerType.includes('choice') ||
                   lowerType.includes('option') ||
                   lowerType.includes('toggle') ||
                   lowerType.includes('switch');
        };
        
        return fields.map((field) => {
            // Исключаем поля выбора (не для ввода текста)
            if (isExcludedFieldType(field.type, field.name)) {
                console.log(`[TranslationService] Fallback: Excluding ${field.type} field "${field.name}" from badges`);
                return true; // Помечаем как "чувствительное" чтобы исключить из бэджей
            }
            
            const type = field.type || '';
            
            // Минимальная техническая фильтрация - только явные технические ограничения
            // Check type (только технические ограничения браузера)
            if (type === 'password' || type === 'hidden') return true;
            
            // Check data-ai-private (явно помеченные разработчиком)
            if (field.isPrivate === true || field['data-ai-private'] === 'true') return true;
            
            // ВСЕ ОСТАЛЬНОЕ РЕШАЕТ GPT - никаких жестко прописанных паттернов!
            return false;
        });
    }

    /** GPT анализ всех полей формы для определения языка */
    private async askGptForLanguage(formData: string): Promise<Lang> {
        console.log('[TranslationService] Making LANGUAGE DETECTION request');
        
        try {
            const res = await fetch(CONFIG.DETECT_LANGUAGE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ formData })
            });

            const data = await res.json();
            const code = data.reply?.trim().toLowerCase() ?? CONFIG.DEFAULT_LANG;
            console.log('[TranslationService] Language detection response:', code);
            
            const VALID = CONFIG.SUPPORTED_LANGS as string[];
            const finalLang = VALID.includes(code) ? code : CONFIG.DEFAULT_LANG;
            console.log('[TranslationService] Final language after validation:', finalLang);
            
            return finalLang as Lang;
        } catch (error) {
            console.error('[TranslationService] Language detection failed:', error);
            return CONFIG.DEFAULT_LANG as Lang;
        }
    }
}